/* eslint-disable jira/react/no-empty-divs */
import React from 'react';
import { styled } from '@compiled/react';
import { Box, xcss } from '@atlaskit/primitives';
import { colors } from '@atlaskit/theme';
import { token } from '@atlaskit/tokens';
import { borderRadius } from '@atlassian/jira-common-styles/src/main.tsx';
import { useIntl } from '@atlassian/jira-intl';
import type { GoalWithId } from '../../../../../../common/types';
import {
	CalendarFieldWrapper,
	JqlFieldWrapper,
	GoalFieldWrapper,
	PriorityFieldWrapper,
	Row,
} from '../../../../../../common/ui/goals/styled';
import { useSLAHierarchicalGoalsEnabled } from '../../../../../../controllers/enable-sla-hierarchical-goals';
import { CalendarViewField } from './calendar';
import { DurationViewField } from './duration';
import { JqlViewField } from './jql';
import messages from './messages';
import { PriorityViewField } from './priority';

type Props = {
	goal: GoalWithId;
};

export const GoalView = ({ goal }: Props) => {
	const {
		id,
		metricId,
		duration: goalDuration,
		calendarName: goalCalendarName,
		jqlQuery: goalJqlQuery,
		defaultGoal,
		subGoals = [],
	} = goal;
	const { formatMessage } = useIntl();
	const [{ isSLAHierarchicalGoalsEnabled }] = useSLAHierarchicalGoalsEnabled();

	return isSLAHierarchicalGoalsEnabled ? (
		<>
			<>
				{!defaultGoal && (
					<GoalContainer>
						<div>
							<JqlViewField
								jqlQuery={goalJqlQuery}
								defaultGoal={defaultGoal}
								goalId={id}
								metricId={metricId}
								areSubGoalsPresent={!!subGoals?.length}
							/>
							<CalendarFieldWrapper />
							<GoalFieldWrapper />
						</div>
						<Box xcss={subGoalContainerStyles}>
							{subGoals.length > 0 && (
								<div>
									<PriorityContainer>{formatMessage(messages.priorityHeader)}</PriorityContainer>
									<>
										{subGoals.map(({ id: subGoalID, calendarName, duration, jqlQuery }) => {
											const [key, value] = jqlQuery.split('=');
											return (
												<RowViewWithFF key={subGoalID}>
													<PriorityFieldWrapper>
														{(key === undefined || key.trim() === 'priority') && (
															<PriorityViewField
																goalId={id}
																metricId={metricId}
																selectedPriority={value.trim()}
															/>
														)}
													</PriorityFieldWrapper>
													<CalendarFieldWrapper>
														{calendarName != null && (
															<CalendarViewField calendarName={calendarName} />
														)}
													</CalendarFieldWrapper>
													<GoalFieldWrapper>
														<DurationViewField duration={duration} />
													</GoalFieldWrapper>
												</RowViewWithFF>
											);
										})}
									</>
								</div>
							)}
							<RowViewWithFF>
								<PriorityFieldWrapper>
									<PriorityViewField
										placeholder={
											subGoals.length === 0
												? formatMessage(messages.allMatchingPriorities)
												: formatMessage(messages.allRemainingPriorities)
										}
									/>
								</PriorityFieldWrapper>
								<CalendarFieldWrapper>
									{goalCalendarName != null && (
										<CalendarViewField calendarName={goalCalendarName} />
									)}
								</CalendarFieldWrapper>
								<GoalFieldWrapper>
									<DurationViewField duration={goalDuration} />
								</GoalFieldWrapper>
							</RowViewWithFF>
						</Box>
					</GoalContainer>
				)}
			</>

			{defaultGoal && (
				<>
					<RowViewWithFF>
						<JqlFieldWrapperWithFF>
							<JqlViewField jqlQuery={goalJqlQuery} defaultGoal={defaultGoal} />
						</JqlFieldWrapperWithFF>
						<CalendarFieldWrapper>
							{goalCalendarName != null && <CalendarViewField calendarName={goalCalendarName} />}
						</CalendarFieldWrapper>
						<GoalFieldWrapper>
							<DurationViewField duration={goalDuration} />
						</GoalFieldWrapper>
					</RowViewWithFF>
				</>
			)}
		</>
	) : (
		<RowView
			data-test-id={`servicedesk-sla-settings.ui.metrics.metric.metric-view.goals.goal.view-${id}`}
		>
			<GoalFieldWrapper>
				<DurationViewField duration={goalDuration} />
			</GoalFieldWrapper>
			<CalendarFieldWrapper>
				{goalCalendarName != null && <CalendarViewField calendarName={goalCalendarName} />}
			</CalendarFieldWrapper>
			<JqlFieldWrapper>
				<JqlViewField jqlQuery={goalJqlQuery} defaultGoal={defaultGoal} />
			</JqlFieldWrapper>
		</RowView>
	);
};

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const RowView = styled(Row)({
	padding: `0 ${token('space.100', '8px')}`,
	boxShadow: token(
		'elevation.shadow.raised',
		'0 1px 1px rgba(9,30,66,0.25), 0 0 1px 1px rgba(9,30,66,0.13)',
	),
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values -- Ignored via go/DSP-18766
	background: token('elevation.surface.raised', colors.N0),
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const GoalContainer = styled.div({
	padding: `${token('space.200', '16px')} ${token('space.100', '8px')}`,
	margin: `${token('space.100', '8px')} 0`,
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values -- Ignored via go/DSP-18766
	border: `1px solid ${token('color.border', colors.DN300A)}`,
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const PriorityContainer = styled.div({
	margin: `${token('space.100', '8px')} 0`,
});

const subGoalContainerStyles = xcss({
	margin: 'space.100',
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const JqlFieldWrapperWithFF = styled.td({
	flexGrow: 2,
	flexShrink: 1,
	flexBasis: token('space.800'),
	paddingRight: token('space.100', '8px'),
	textOverflow: 'ellipsis',
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const RowViewWithFF = styled.tr({
	display: 'flex',
	alignItems: 'center',
	boxShadow: token(
		'elevation.shadow.raised',
		'0 1px 1px rgba(9,30,66,0.25), 0 0 1px 1px rgba(9,30,66,0.13)',
	),
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values, @atlaskit/ui-styling-standard/no-unsafe-values -- Ignored via go/DSP-18766
	borderRadius: `${borderRadius}px`,
	flexDirection: 'row',
	minHeight: token('space.500'),
	marginBottom: token('space.100', '8px'),
	padding: `0 ${token('space.100', '8px')}`,
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values -- Ignored via go/DSP-18766
	background: token('elevation.surface.raised', colors.N0),
});
